/**
 * Brand derivation for the Website Studio preview — a verbatim port of
 * the portal's deriveBrandVars numerics (apps/portal/src/lib/brand.ts;
 * fourth copy of this math after portal / work-driver / the original —
 * keep them numerically identical, noted for future consolidation).
 * Pure: no DOM, unit-testable — the preset WCAG test lives on this.
 */

export type Rgb = [number, number, number];

export function parseHex(hex: string): Rgb | null {
  const m = /^#([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return null;
  const n = parseInt(m[1]!, 16);
  return [(n >> 16) & 0xff, (n >> 8) & 0xff, n & 0xff];
}

export function toHex([r, g, b]: Rgb): string {
  const p = (v: number) => Math.round(v).toString(16).padStart(2, '0');
  return `#${p(r)}${p(g)}${p(b)}`;
}

function mix(a: Rgb, b: Rgb, t: number): Rgb {
  return [
    a[0] + (b[0] - a[0]) * t,
    a[1] + (b[1] - a[1]) * t,
    a[2] + (b[2] - a[2]) * t,
  ];
}

function luminance([r, g, b]: Rgb): number {
  const lin = (v: number) => {
    const s = v / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
}

export function contrastRatio(a: Rgb, b: Rgb): number {
  const la = luminance(a);
  const lb = luminance(b);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}

const WHITE: Rgb = [255, 255, 255];
const BLACK: Rgb = [0, 0, 0];

function hover(c: Rgb): Rgb {
  return luminance(c) > 0.35 ? mix(c, BLACK, 0.12) : mix(c, WHITE, 0.1);
}

function wash(c: Rgb): Rgb {
  return mix(c, WHITE, 0.88);
}

export interface AccentInkResult {
  ink: Rgb;
  /** True when no tint of the accent itself cleared 4.5:1 and we fell
   *  back to hard black/white — the free-picker warning trigger. */
  usedFallback: boolean;
}

export function accentInkWalk(accent: Rgb): AccentInkResult {
  const towards = luminance(accent) > 0.35 ? BLACK : WHITE;
  for (let t = 0.5; t <= 1.001; t += 0.05) {
    const candidate = mix(accent, towards, t);
    if (contrastRatio(candidate, accent) >= 4.5) {
      return { ink: candidate, usedFallback: false };
    }
  }
  return {
    ink:
      contrastRatio(BLACK, accent) >= contrastRatio(WHITE, accent) ? BLACK : WHITE,
    usedFallback: true,
  };
}

/** The portal's seven Tier-C custom properties for a draft token pair. */
export function deriveBrandVars(
  primaryHex: string | null,
  accentHex: string | null,
): Record<string, string> {
  const vars: Record<string, string> = {};
  const primary = primaryHex ? parseHex(primaryHex) : null;
  if (primary) {
    vars['--brand-primary'] = toHex(primary);
    vars['--brand-primary-hover'] = toHex(hover(primary));
    vars['--brand-primary-wash'] = toHex(wash(primary));
  }
  const accent = accentHex ? parseHex(accentHex) : null;
  if (accent) {
    vars['--brand-accent'] = toHex(accent);
    vars['--brand-accent-hover'] = toHex(hover(accent));
    vars['--brand-accent-wash'] = toHex(wash(accent));
    vars['--brand-accent-ink'] = toHex(accentInkWalk(accent).ink);
  }
  return vars;
}

/** Free-picker guidance: warn (never block) when the ink walk fell back. */
export function contrastWarning(accentHex: string | null): boolean {
  const accent = accentHex ? parseHex(accentHex) : null;
  if (!accent) return false;
  return accentInkWalk(accent).usedFallback;
}
